import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Play,
  Pause,
  Stop,
  X,
  DownloadSimple,
  Clock,
  FastForward,
} from "@phosphor-icons/react";

const SPEEDS = [
  { label: "0.5x", ms: 2000 },
  { label: "1x", ms: 1000 },
  { label: "2x", ms: 500 },
  { label: "4x", ms: 250 },
];

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function daysBetween(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(0, Math.round((e - s) / 86400000));
}

function getDefaultStart() {
  const d = new Date();
  d.setDate(d.getDate() - 33);
  return d.toISOString().split("T")[0];
}

function getDefaultEnd() {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString().split("T")[0];
}

export default function TimeLapseControl({
  currentDate,
  onDateChange,
  onClose,
  onDownload,
  activeLayerId,
}) {
  const [startDate, setStartDate] = useState(getDefaultStart);
  const [endDate, setEndDate] = useState(getDefaultEnd);
  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const [dayIndex, setDayIndex] = useState(0);
  const intervalRef = useRef(null);
  const totalDays = daysBetween(startDate, endDate);

  const currentPlayDate = addDays(startDate, dayIndex);

  // Step size: 1 day for true color/LST, 4 days for NDVI (16-day composite)
  const step = activeLayerId === "ndvi" ? 4 : 1;

  const stopPlayback = useCallback(() => {
    setPlaying(false);
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const startPlayback = useCallback(() => {
    if (dayIndex >= totalDays) setDayIndex(0);
    setPlaying(true);
  }, [dayIndex, totalDays]);

  // Playback loop
  useEffect(() => {
    if (!playing) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    intervalRef.current = setInterval(() => {
      setDayIndex((prev) => {
        const next = prev + step;
        if (next > totalDays) {
          setPlaying(false);
          return totalDays;
        }
        return next;
      });
    }, SPEEDS[speedIdx].ms);

    return () => clearInterval(intervalRef.current);
  }, [playing, speedIdx, totalDays, step]);

  // Sync date to parent whenever dayIndex changes
  useEffect(() => {
    onDateChange(currentPlayDate);
  }, [dayIndex, currentPlayDate, onDateChange]);

  const handleSliderChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setDayIndex(val);
    if (playing) stopPlayback();
  };

  const handleReset = () => {
    stopPlayback();
    setDayIndex(0);
  };

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="absolute bottom-0 left-0 right-0 z-[960] bg-stone-900/95 backdrop-blur-xl border-t border-stone-700"
      data-testid="timelapse-control"
    >
      {/* Progress bar */}
      <div className="h-1 bg-stone-800">
        <div
          className="h-full bg-emerald-500 transition-all duration-200"
          style={{ width: totalDays > 0 ? `${(dayIndex / totalDays) * 100}%` : "0%" }}
        />
      </div>

      <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
        {/* Close */}
        <button
          onClick={() => {
            stopPlayback();
            onClose();
          }}
          className="text-stone-400 hover:text-white p-1 rounded-lg hover:bg-stone-800 transition-colors shrink-0"
          data-testid="timelapse-close-btn"
        >
          <X size={16} />
        </button>

        <div className="w-px h-6 bg-stone-700 shrink-0" />

        {/* Play controls */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={playing ? stopPlayback : startPlayback}
            className={`p-2 rounded-lg transition-colors ${
              playing
                ? "bg-emerald-600 text-white"
                : "bg-stone-800 text-stone-300 hover:bg-stone-700 hover:text-white"
            }`}
            data-testid="timelapse-play-btn"
          >
            {playing ? <Pause size={16} weight="bold" /> : <Play size={16} weight="bold" />}
          </button>
          <button
            onClick={handleReset}
            className="p-2 rounded-lg bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-white transition-colors"
            data-testid="timelapse-stop-btn"
          >
            <Stop size={16} weight="bold" />
          </button>
        </div>

        {/* Speed */}
        <div className="flex items-center gap-1 shrink-0">
          <FastForward size={14} className="text-stone-500" />
          {SPEEDS.map((s, i) => (
            <button
              key={s.label}
              onClick={() => setSpeedIdx(i)}
              className={`text-[10px] px-2 py-1 rounded-md font-body transition-colors ${
                speedIdx === i
                  ? "bg-emerald-600/20 text-emerald-400 border border-emerald-600/40"
                  : "text-stone-500 hover:text-stone-300"
              }`}
              data-testid={`speed-${s.label}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-stone-700 shrink-0 hidden sm:block" />

        {/* Date range */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setDayIndex(0);
              stopPlayback();
            }}
            className="bg-stone-800 border border-stone-700 text-white text-[11px] rounded-lg px-2 py-1 focus:outline-none focus:border-emerald-600 w-[120px]"
            data-testid="timelapse-start-date"
          />
          <span className="text-stone-600 text-xs">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setDayIndex(0);
              stopPlayback();
            }}
            className="bg-stone-800 border border-stone-700 text-white text-[11px] rounded-lg px-2 py-1 focus:outline-none focus:border-emerald-600 w-[120px]"
            data-testid="timelapse-end-date"
          />
        </div>

        {/* Timeline slider */}
        <div className="flex-1 min-w-[80px] mx-2">
          <input
            type="range"
            min={0}
            max={totalDays}
            value={dayIndex}
            onChange={handleSliderChange}
            className="w-full h-1.5 rounded-full appearance-none bg-stone-700 cursor-pointer accent-emerald-500"
            style={{
              background: `linear-gradient(to right, #10b981 ${
                totalDays > 0 ? (dayIndex / totalDays) * 100 : 0
              }%, #44403c ${totalDays > 0 ? (dayIndex / totalDays) * 100 : 0}%)`,
            }}
            data-testid="timelapse-slider"
          />
        </div>

        {/* Current date display */}
        <div className="flex items-center gap-1.5 shrink-0 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5">
          <Clock size={12} className="text-emerald-400" />
          <span className="text-xs text-white font-mono font-medium" data-testid="timelapse-current-date">
            {currentPlayDate}
          </span>
        </div>

        <div className="w-px h-6 bg-stone-700 shrink-0" />

        {/* Download */}
        <button
          onClick={() => onDownload(startDate, endDate)}
          className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors shrink-0"
          data-testid="timelapse-download-btn"
        >
          <DownloadSimple size={14} weight="bold" />
          <span className="hidden md:inline">Download</span>
        </button>
      </div>

      {/* Info line */}
      <div className="px-6 pb-2 flex items-center gap-4 text-[10px] text-stone-500">
        <span>
          Day {dayIndex + 1} of {totalDays + 1}
        </span>
        <span>Step: {step} day{step > 1 ? "s" : ""}</span>
        <span>{totalDays + 1} frames total</span>
      </div>
    </motion.div>
  );
}
